import { useState, useEffect, useRef } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { IconRotateClockwise, IconLoader2, IconScan, IconPhoto } from '@tabler/icons-react';
import EditorModalShell, {
  EditorBarFrame,
  EditorBarTools,
  EditorBarSecondary,
  EditorBarPrimary,
} from '@/components/common/EditorModalShell';

// Full-screen document-crop modal for OCR — built on the app's shared EditorModalShell
// (same design as the photo-crop modal: logo header, red close, black canvas, gradient
// primary + neutral tool blocks). Free-form crop via react-image-crop. Input/output is
// a base64 data URL so the wizard is unchanged; the cropped region is downscaled to a
// legible-but-lean JPEG for the vision model.

// Default selection = the FULL image; the user shrinks it only if needed.
const centeredFreeCrop = () => ({ unit: '%', x: 0, y: 0, width: 100, height: 100 });

// Clamp a percent crop rect into valid bounds (used for the auto-detected initial box).
function clampPercentCrop(c) {
  const x = Math.max(0, Math.min(100, c.x || 0));
  const y = Math.max(0, Math.min(100, c.y || 0));
  const width = Math.max(1, Math.min(100 - x, c.width || 0));
  const height = Math.max(1, Math.min(100 - y, c.height || 0));
  return { unit: '%', x, y, width, height };
}

function rotateDataUrl(src, rotation) {
  return new Promise((resolve) => {
    if (!rotation) { resolve(src); return; }
    const img = new window.Image();
    img.onload = () => {
      const swap = rotation === 90 || rotation === 270;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const canvas = document.createElement('canvas');
      canvas.width = swap ? h : w;
      canvas.height = swap ? w : h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(img, -w / 2, -h / 2);
      resolve(canvas.toDataURL('image/jpeg', 0.92));
    };
    img.onerror = () => resolve(src);
    img.src = src;
  });
}

// The AI scan needs the cropped region to stay SHARP — NID fine print (NID number,
// MRZ, address) must remain legible — so we keep near-native resolution instead of
// shrinking it. A high long-edge cap only bounds an extreme phone photo; we compress
// (quality, then size) ONLY when the encoded image exceeds MAX_SCAN_BYTES (~5MB). The
// vision endpoint itself accepts up to 20MB.
const MAX_SCAN_EDGE = 2000;                 // px — plenty for NID text; was 304 (blurry)
const MAX_SCAN_BYTES = 5 * 1024 * 1024;     // only compress past this

// Approx decoded byte size of a base64 data URL (payload chars × 3/4).
const dataUrlBytes = (u) => Math.ceil(((u.length - (u.indexOf(',') + 1)) * 3) / 4);

// Encode a canvas to a JPEG data URL at high quality; step quality (then dimensions)
// down only if it exceeds the 5MB budget — so a normal NID crop stays full quality.
function encodeUnderLimit(canvasIn) {
  let canvas = canvasIn;
  let q = 0.92;
  let url = canvas.toDataURL('image/jpeg', q);
  while (dataUrlBytes(url) > MAX_SCAN_BYTES && q > 0.5) {
    q = Math.round((q - 0.1) * 100) / 100;
    url = canvas.toDataURL('image/jpeg', q);
  }
  while (dataUrlBytes(url) > MAX_SCAN_BYTES && canvas.width > 800) {
    const c2 = document.createElement('canvas');
    c2.width = Math.round(canvas.width * 0.85);
    c2.height = Math.round(canvas.height * 0.85);
    const cx = c2.getContext('2d');
    cx.imageSmoothingEnabled = true;
    cx.imageSmoothingQuality = 'high';
    cx.fillStyle = '#ffffff';
    cx.fillRect(0, 0, c2.width, c2.height);
    cx.drawImage(canvas, 0, 0, c2.width, c2.height);
    canvas = c2;
    url = canvas.toDataURL('image/jpeg', q);
  }
  return url;
}

function cropToDataUrl(imgEl, pct) {
  const nw = imgEl.naturalWidth;
  const nh = imgEl.naturalHeight;
  const sx = (pct.x / 100) * nw;
  const sy = (pct.y / 100) * nh;
  const sw = (pct.width / 100) * nw;
  const sh = (pct.height / 100) * nh;
  if (sw < 1 || sh < 1) return null;
  // Keep native resolution (never upscale); only bound an extreme long edge.
  const scale = Math.min(1, MAX_SCAN_EDGE / Math.max(sw, sh));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(sw * scale));
  canvas.height = Math.max(1, Math.round(sh * scale));
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(imgEl, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
  return encodeUnderLimit(canvas);
}

// "Use full" — the whole (uncropped) image at the same near-native, 5MB-guarded size.
function downscaleDataUrl(src) {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_SCAN_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(encodeUnderLimit(canvas));
    };
    img.onerror = () => resolve(src);
    img.src = src;
  });
}

export default function ScanCropDialog({ open, imageSrc, initialCrop = null, onCancel, onConfirm, lang = 'en', busy = false }) {
  const isBn = lang === 'bn';
  const imgRef = useRef(null);
  const startedRef = useRef('');
  const [src, setSrc] = useState('');
  const [rotation, setRotation] = useState(0);
  const [crop, setCrop] = useState();
  const [completed, setCompleted] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const on = () => setIsMobile(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  useEffect(() => { if (open) setRotation(0); }, [open, imageSrc]);

  useEffect(() => {
    if (!open || !imageSrc) { setSrc(''); return undefined; }
    let alive = true;
    startedRef.current = '';
    setCrop(undefined);
    setCompleted(null);
    rotateDataUrl(imageSrc, rotation).then((s) => { if (alive) setSrc(s); });
    return () => { alive = false; };
  }, [open, imageSrc, rotation]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [open, onCancel]);

  const startCrop = (node) => {
    if (!src || startedRef.current === src) return;
    if (!node?.naturalWidth) return;
    startedRef.current = src;
    // Start from the auto-detected card box (adjustable) when we have one and the image
    // isn't rotated; otherwise select the whole image.
    const c = (rotation === 0 && initialCrop) ? clampPercentCrop(initialCrop) : centeredFreeCrop();
    setCrop(c);
    setCompleted(c);
  };

  const doScan = () => {
    if (imgRef.current && completed?.width && completed?.height) {
      const url = cropToDataUrl(imgRef.current, completed);
      if (url) { onConfirm(url); return; }
    }
    useFull();
  };

  const useFull = async () => {
    onConfirm(await downscaleDataUrl(src || imageSrc));
  };

  if (!open) return null;

  const canvas = src ? (
    <ReactCrop crop={crop} onChange={(c) => setCrop(c)} onComplete={(_, pct) => setCompleted(pct)} keepSelection ruleOfThirds style={{ maxWidth: '100%', maxHeight: '100%' }}>
      <img
        ref={(n) => { imgRef.current = n; if (n?.complete && n.naturalWidth) startCrop(n); }}
        src={src}
        alt=""
        onLoad={(e) => startCrop(e.currentTarget)}
        style={{ display: 'block', maxHeight: isMobile ? 'calc(100vh - 210px)' : 'calc(100vh - 190px)', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }}
        draggable={false}
      />
    </ReactCrop>
  ) : (
    <div className="text-white/70"><IconLoader2 className="animate-spin" size={26} /></div>
  );

  const rotateBtn = (
    <button
      type="button"
      onClick={() => setRotation((r) => (r + 90) % 360)}
      className="inline-flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold text-zinc-700 dark:text-zinc-200 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
      style={{ width: 74, border: 'none', borderRight: '1px solid var(--color-border, #e4e4e7)', borderRadius: 0, cursor: 'pointer' }}
    >
      <IconRotateClockwise size={18} />
      {isBn ? 'ঘোরান' : 'Rotate'}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[200] flex flex-col bg-white dark:bg-zinc-900" role="dialog" aria-modal="true">
      <EditorModalShell
        isMobile={isMobile}
        icon={<IconScan size={20} />}
        title={isBn ? 'স্ক্যান করতে ডকুমেন্ট ক্রপ করুন' : 'Crop the document to scan'}
        onClose={onCancel}
        canvas={canvas}
        bottomBar={
          <EditorBarFrame isMobile={isMobile}>
            {isMobile ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                  className="inline-flex items-center justify-center gap-1 rounded-2xl bg-zinc-100 dark:bg-zinc-800 px-3 py-3 text-sm font-semibold text-zinc-700 dark:text-zinc-200"
                >
                  <IconRotateClockwise size={18} />
                </button>
                <button
                  type="button"
                  onClick={useFull}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-2xl bg-zinc-100 dark:bg-zinc-800 px-3 py-3 text-sm font-semibold text-zinc-700 dark:text-zinc-200"
                >
                  <IconPhoto size={16} />
                  {isBn ? 'পুরো ছবি' : 'Use full'}
                </button>
                <button
                  type="button"
                  onClick={doScan}
                  disabled={busy}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-2xl px-3 py-3 text-sm font-bold text-white disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg,#FBA53A,#E68A00)' }}
                >
                  {busy ? <IconLoader2 size={16} className="animate-spin" /> : <IconScan size={16} />}
                  {isBn ? 'স্ক্যান' : 'Scan'}
                </button>
              </div>
            ) : (
              <>
                <EditorBarTools>{rotateBtn}</EditorBarTools>
                <EditorBarSecondary onClick={useFull} title={isBn ? 'পুরো ছবি' : 'Use full'}>
                  <IconPhoto size={16} />
                  {isBn ? 'পুরো ছবি' : 'Use full'}
                </EditorBarSecondary>
                <EditorBarPrimary onClick={doScan} disabled={busy}>
                  {busy ? <IconLoader2 size={16} className="animate-spin" /> : <IconScan size={16} />}
                  {isBn ? 'স্ক্যান' : 'Scan'}
                </EditorBarPrimary>
              </>
            )}
          </EditorBarFrame>
        }
      />
    </div>
  );
}
